import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect } from "react";
import { Brain, Plus, Trash2, Loader2, X, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";

type ReflectionModel = "gibbs" | "rolfe";

const GIBBS_STAGES = [
  { key: "description", label: "Description", prompt: "What happened? Describe the situation, event, or clinical incident objectively." },
  { key: "feelings", label: "Feelings", prompt: "What were you thinking and feeling at the time? How did you react initially?" },
  { key: "evaluation", label: "Evaluation", prompt: "What was good and bad about the experience? What went well, what didn't?" },
  { key: "analysis", label: "Analysis", prompt: "What sense can you make of the situation? Why did things happen as they did?" },
  { key: "conclusion", label: "Conclusion", prompt: "What else could you have done? What have you learnt from this experience?" },
  { key: "action_plan", label: "Action Plan", prompt: "If this arose again, what would you do differently? What CPD will you undertake?" },
];

const ROLFE_STAGES = [
  { key: "what", label: "What?", prompt: "What happened? Describe the situation — what did you do, observe, or experience?" },
  { key: "so_what", label: "So what?", prompt: "So what does this mean? What is the significance of the situation? What have you learnt?" },
  { key: "now_what", label: "Now what?", prompt: "Now what do you need to do? What action will you take? How will this change your practice?" },
];

interface ReflectionEntry {
  id: number;
  title: string;
  date: string;
  hours: number;
  learningNeed: string;
  activityUndertaken: string;
  outcome: string;
  patientImpact: string;
  createdAt: string;
}

export default function ReflectionTool() {
  const [model, setModel] = useState<ReflectionModel>("gibbs");
  const [entries, setEntries] = useState<ReflectionEntry[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const stages = model === "gibbs" ? GIBBS_STAGES : ROLFE_STAGES;
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formTitle, setFormTitle] = useState("");
  const [formDate, setFormDate] = useState(new Date().toISOString().split("T")[0]);

  const load = async () => {
    const res = await fetch(`/api/physio/cpd?year=${new Date().getFullYear()}`);
    if (res.ok) {
      const data: ReflectionEntry[] = await res.json();
      setEntries(data.filter(a => a.learningNeed?.toLowerCase().includes("reflect") || a.activityUndertaken?.toLowerCase().includes("gibbs") || a.activityUndertaken?.toLowerCase().includes("rolfe")));
    }
  };

  useEffect(() => { load(); }, []);

  const updateFormData = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const stagesText = stages.map(s => `${s.label}: ${formData[s.key] ?? ""}`).join("\n\n");
    try {
      const res = await fetch("/api/physio/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: formTitle.trim(),
          date: formDate,
          category: "professional",
          hours: 0.5,
          learningNeed: `Structured reflection — ${model === "gibbs" ? "Gibbs" : "Rolfe"} cycle`,
          activityUndertaken: stagesText,
          outcome: formData[model === "gibbs" ? "conclusion" : "so_what"] ?? "Completed structured reflection",
          patientImpact: formData[model === "gibbs" ? "action_plan" : "now_what"] ?? "Actions identified to improve patient care",
        }),
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({});
        setFormTitle("");
        load();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this reflection entry?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/physio/cpd/${id}`, { method: "DELETE" });
      setEntries(prev => prev.filter(a => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PhysioLayout title="Structured Reflection Tool">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Brain className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">Structured Reflection</h2>
              <p className="text-emerald-300 text-sm">Gibbs or Rolfe Cycle · Supervision & Clinical Incidents</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            Structured reflection supports your professional development and HCPC CPD requirements. Choose a reflection model and work through guided prompts. Entries are saved as Professional CPD activities.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Choose reflection model</p>
            <p className="text-xs text-slate-500">Both models are widely used in UK healthcare. Gibbs (1988) is more detailed; Rolfe (2001) is more concise.</p>
          </div>
          <div className="flex gap-2">
            {(["gibbs", "rolfe"] as ReflectionModel[]).map((m) => (
              <button
                key={m}
                onClick={() => setModel(m)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  model === m
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {m === "gibbs" ? "Gibbs Cycle" : "Rolfe et al."}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900">{model === "gibbs" ? "Gibbs Reflective Cycle (1988)" : "Rolfe et al. Framework (2001)"}</h3>
            {!showForm && (
              <Button onClick={() => setShowForm(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
                <Plus className="h-4 w-4" /> New Reflection
              </Button>
            )}
          </div>

          {model === "gibbs" && (
            <div className="mb-4 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              <p className="text-xs text-emerald-800 leading-relaxed">
                Gibbs' Reflective Cycle moves through six stages: <strong>Description → Feelings → Evaluation → Analysis → Conclusion → Action Plan</strong>. Work through each stage to build a complete reflection.
              </p>
            </div>
          )}
          {model === "rolfe" && (
            <div className="mb-4 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              <p className="text-xs text-emerald-800 leading-relaxed">
                Rolfe's framework uses three powerful questions: <strong>What? → So what? → Now what?</strong> — concise and flexible for busy practitioners.
              </p>
            </div>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 border border-emerald-200 bg-emerald-50 rounded-xl p-5 mb-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-emerald-800 text-sm">New {model === "gibbs" ? "Gibbs" : "Rolfe"} Reflection</span>
                <button type="button" onClick={() => { setShowForm(false); setFormData({}); }}><X className="h-4 w-4 text-slate-400" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label htmlFor="physio-ref-title" className="block text-xs font-medium text-slate-600 mb-1">Title / Topic *</label>
                  <input id="physio-ref-title" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="e.g. Supervision discussion — complex RA flare management" required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label htmlFor="physio-ref-date" className="block text-xs font-medium text-slate-600 mb-1">Date *</label>
                  <input id="physio-ref-date" type="date" value={formDate} onChange={e => setFormDate(e.target.value)} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              {stages.map((stage, i) => (
                <div key={stage.key}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-slate-700">
                      <span className="text-emerald-700 mr-1">{i + 1}.</span> {stage.label}
                    </label>
                    <VoiceInputButton
                      tone="teal"
                      size="xs"
                      onTranscript={(chunk) => updateFormData(stage.key, appendVoiceChunk(formData[stage.key] ?? "", chunk))}
                      label={`Dictate ${stage.label}`}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mb-1.5 italic">{stage.prompt}</p>
                  <textarea
                    value={formData[stage.key] ?? ""}
                    onChange={e => updateFormData(stage.key, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none bg-white"
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {saving ? "Saving..." : "Save Reflection"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setFormData({}); }} className="text-sm">Cancel</Button>
              </div>
            </form>
          )}

          {entries.length === 0 && !showForm ? (
            <p className="text-sm text-slate-500 text-center py-6">No reflection entries yet. Start a new reflection to begin building your supervised practice log.</p>
          ) : (
            <div className="space-y-2">
              {entries.map((e) => {
                const isOpen = expandedId === e.id;
                return (
                  <div key={e.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button onClick={() => setExpandedId(isOpen ? null : e.id)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50 transition-colors">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{e.title}</p>
                        <p className="text-xs text-slate-500">{new Date(e.date).toLocaleDateString("en-GB")} · {e.learningNeed}</p>
                      </div>
                      {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">Activity / Reflection Content</p>
                          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{e.activityUndertaken}</p>
                        </div>
                        <div className="flex justify-end">
                          <button onClick={() => handleDelete(e.id)} disabled={deletingId === e.id} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
                            {deletingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>References:</strong> Gibbs, G. (1988). <em>Learning by Doing: A guide to teaching and learning methods.</em> Further Education Unit, Oxford. Rolfe, G., Freshwater, D., & Jasper, M. (2001). <em>Critical Reflection in Nursing and the Helping Professions.</em> Palgrave Macmillan.
        </p>
      </div>
    </PhysioLayout>
  );
}
